import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { adminAPI } from '../services/api';

const FloatingChatContext = createContext();

export const useFloatingChat = () => {
  const context = useContext(FloatingChatContext);
  if (!context) {
    throw new Error('useFloatingChat must be used within a FloatingChatProvider');
  }
  return context;
};

export const FloatingChatProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    whatsapp_enabled: true,
    ai_chat_enabled: true,
    whatsapp_button_text: 'WhatsApp Support',
    ai_button_text: 'AI Assistant',
    whatsapp_button_color: '#25D366',
    ai_button_color: '#667eea',
    position: 'bottom-right',
    z_index: 50,
    show_on_mobile: true,
    show_on_desktop: true,
    auto_hide: false,
    auto_hide_delay: 5000,
    page_visibility: {
      home: true,
      about: true,
      services: true,
      products: true,
      portfolio: true,
      blog: true,
      contact: true,
      quote: true,
      appointment: true,
      cart: true,
      checkout: true,
      dashboard: false,
      admin: false
    }
  });

  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getFloatingChatSettings();
      if (response.data?.success) {
        setSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error loading floating chat settings:', error);
      // Use default settings if API fails
    } finally {
      setLoading(false);
    }
  };

  const shouldShowButtons = () => {
    if (loading) return false;

    // Check if buttons are enabled
    if (!settings.whatsapp_enabled && !settings.ai_chat_enabled) {
      return false;
    }

    // Check device type
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (isMobile && !settings.show_on_mobile) return false;
    if (!isMobile && !settings.show_on_desktop) return false;

    // Check current page visibility
    const currentPath = location.pathname;
    let pageKey = 'home'; // default

    // Map routes to page keys
    if (currentPath === '/') pageKey = 'home';
    else if (currentPath === '/about') pageKey = 'about';
    else if (currentPath === '/services') pageKey = 'services';
    else if (currentPath === '/products') pageKey = 'products';
    else if (currentPath === '/portfolio') pageKey = 'portfolio';
    else if (currentPath === '/blog') pageKey = 'blog';
    else if (currentPath === '/contact') pageKey = 'contact';
    else if (currentPath === '/quote') pageKey = 'quote';
    else if (currentPath === '/appointment') pageKey = 'appointment';
    else if (currentPath === '/cart') pageKey = 'cart';
    else if (currentPath === '/checkout') pageKey = 'checkout';
    else if (currentPath.startsWith('/dashboard')) pageKey = 'dashboard';
    else if (currentPath.startsWith('/admin')) pageKey = 'admin';
    else pageKey = 'home'; // default for other pages

    return settings.page_visibility[pageKey] || false;
  };

  const getButtonPosition = () => {
    const position = settings.position || 'bottom-right';
    const baseStyles = {
      position: 'fixed',
      zIndex: settings.z_index || 50
    };

    switch (position) {
      case 'bottom-right':
        return { ...baseStyles, bottom: '1.5rem', right: '1.5rem' };
      case 'bottom-left':
        return { ...baseStyles, bottom: '1.5rem', left: '1.5rem' };
      case 'top-right':
        return { ...baseStyles, top: '1.5rem', right: '1.5rem' };
      case 'top-left':
        return { ...baseStyles, top: '1.5rem', left: '1.5rem' };
      default:
        return { ...baseStyles, bottom: '1.5rem', right: '1.5rem' };
    }
  };

  const value = {
    settings,
    loading,
    shouldShowButtons,
    getButtonPosition,
    refreshSettings: loadSettings
  };

  return (
    <FloatingChatContext.Provider value={value}>
      {children}
    </FloatingChatContext.Provider>
  );
};

export default FloatingChatContext;
